import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Plus, Edit, Trash2, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";

const AdminCompetitions = () => {
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingComp, setEditingComp] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    short_name: "",
    season: "",
    type: "league",
    description: "",
    logo_url: "",
    start_date: "",
    end_date: "",
    status: "active"
  });

  const fetchCompetitions = async () => {
    try {
      const response = await axios.get(`${API}/competitions`);
      setCompetitions(response.data);
    } catch (error) {
      console.error("Error fetching competitions:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompetitions();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingComp) {
        await axios.put(`${API}/competitions/${editingComp.competition_id}`, formData, { withCredentials: true });
        toast.success("Competition updated successfully");
      } else {
        await axios.post(`${API}/competitions`, formData, { withCredentials: true });
        toast.success("Competition created successfully");
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchCompetitions();
    } catch (error) {
      console.error("Error saving competition:", error);
      toast.error("Failed to save competition");
    }
  };

  const handleEdit = (comp) => {
    setEditingComp(comp);
    setFormData({
      name: comp.name || "",
      short_name: comp.short_name || "",
      season: comp.season || "",
      type: comp.type || "league",
      description: comp.description || "",
      logo_url: comp.logo_url || "",
      start_date: comp.start_date || "",
      end_date: comp.end_date || "",
      status: comp.status || "active"
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (compId) => {
    if (!window.confirm("Are you sure you want to delete this competition?")) return;
    
    try {
      await axios.delete(`${API}/competitions/${compId}`, { withCredentials: true });
      toast.success("Competition deleted successfully");
      fetchCompetitions();
    } catch (error) {
      console.error("Error deleting competition:", error);
      toast.error("Failed to delete competition");
    }
  };

  const resetForm = () => {
    setEditingComp(null);
    setFormData({
      name: "",
      short_name: "",
      season: "",
      type: "league",
      description: "",
      logo_url: "",
      start_date: "",
      end_date: "",
      status: "active"
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-[#009E60] text-white",
      upcoming: "bg-[#FFC72C] text-black",
      completed: "bg-gray-500 text-white"
    };
    return (
      <span className={`px-2 py-1 text-xs font-bold uppercase ${styles[status] || styles.upcoming}`}>
        {status}
      </span>
    );
  };

  return (
    <AdminLayout title="Competitions">
      <div data-testid="admin-competitions">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <p className="text-gray-500">Manage leagues, cups, and tournaments</p>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#FFC72C] hover:bg-[#E6B325] text-black rounded-none" data-testid="add-competition-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Competition
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold uppercase">
                  {editingComp ? "Edit Competition" : "Add New Competition"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="competition-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Competition Name *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="rounded-none"
                      data-testid="comp-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Short Name *</label>
                    <Input
                      name="short_name"
                      value={formData.short_name}
                      onChange={handleChange}
                      required
                      className="rounded-none"
                      placeholder="e.g., Premier"
                      data-testid="comp-short-name-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Season *</label>
                    <Input
                      name="season"
                      value={formData.season}
                      onChange={handleChange}
                      required
                      className="rounded-none"
                      placeholder="2024/25"
                      data-testid="comp-season-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Type *</label>
                    <Select value={formData.type} onValueChange={(v) => setFormData({...formData, type: v})}>
                      <SelectTrigger className="rounded-none" data-testid="comp-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="league">League</SelectItem>
                        <SelectItem value="cup">Cup</SelectItem>
                        <SelectItem value="friendly">Friendly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Status</label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                    <SelectTrigger className="rounded-none" data-testid="comp-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="upcoming">Upcoming</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <ImageUploader
                  value={formData.logo_url}
                  onChange={(url) => setFormData({ ...formData, logo_url: url })}
                  category="competition-logo"
                  label="Competition Logo"
                  previewShape="square"
                  testId="comp-logo"
                />
                <div>
                  <label className="text-sm font-medium">Description</label>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="rounded-none"
                    rows={3}
                    data-testid="comp-description-input"
                  />
                </div>
                <div className="flex gap-4">
                  <Button type="submit" className="flex-1 bg-[#FFC72C] hover:bg-[#E6B325] text-black rounded-none" data-testid="comp-submit-btn">
                    {editingComp ? "Update Competition" : "Create Competition"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-none">
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Competitions List */}
        {loading ? (
          <div className="space-y-4">
            {[1,2,3].map(i => (
              <div key={i} className="h-24 bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : competitions.length > 0 ? (
          <div className="space-y-4">
            {competitions.map(comp => (
              <div key={comp.competition_id} className="bg-white border border-gray-200 p-6 flex items-center justify-between group" data-testid={`admin-comp-card-${comp.competition_id}`}>
                <div className="flex items-center gap-6">
                  <div className="w-16 h-16 bg-gray-100 flex items-center justify-center">
                    <Trophy className="w-8 h-8 text-[#FFC72C]" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-heading font-bold text-xl uppercase text-[#09090b]">{comp.name}</h3>
                      {getStatusBadge(comp.status)}
                    </div>
                    <p className="text-sm text-gray-500">
                      {comp.season} • {comp.type}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(comp)}
                    className="rounded-none"
                    data-testid={`edit-comp-${comp.competition_id}`}
                  >
                    <Edit className="w-4 h-4 mr-2" /> Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDelete(comp.competition_id)}
                    className="rounded-none"
                    data-testid={`delete-comp-${comp.competition_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-white border border-gray-200">
            <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-gray-400 uppercase mb-2">No Competitions Yet</h3>
            <p className="text-gray-500 mb-4">Add your first competition to get started.</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCompetitions;
